import { useEffect, useState } from 'react';
import { msParaProximoIntento } from './progresoDerivado';

/**
 * Cuánto falta para poder volver a rendir, como estado que baja solo.
 *
 * **Esto es sólo el contador.** La espera de verdad la hace cumplir el servidor
 * en `academy_rendir_examen`, contra `now()` de la base
 * (ADR-2026-08-25-la-academia-vive-en-supabase). Antes esta cuenta era la única
 * barrera y se salteaba borrando `localStorage`; ahora, si el navegador se
 * equivoca, el intento rebota igual.
 *
 * Tick de un segundo: la espera es de dos horas, pero el último minuto se mira,
 * y un contador que salta de a un minuto se siente roto justo cuando más se lo
 * observa. El intervalo se corta solo al llegar a cero.
 */
export function useEsperaExamen(
  ultimoIntento: number | null,
  esperaMinutos: number,
): { listo: boolean; restanteMs: number; etiqueta: string } {
  const [restanteMs, setRestanteMs] = useState(() =>
    msParaProximoIntento(ultimoIntento, esperaMinutos),
  );

  useEffect(() => {
    setRestanteMs(msParaProximoIntento(ultimoIntento, esperaMinutos));
    if (msParaProximoIntento(ultimoIntento, esperaMinutos) === 0) return;

    const id = setInterval(() => {
      const falta = msParaProximoIntento(ultimoIntento, esperaMinutos);
      setRestanteMs(falta);
      if (falta === 0) clearInterval(id);
    }, 1000);

    return () => clearInterval(id);
  }, [ultimoIntento, esperaMinutos]);

  return {
    listo: restanteMs === 0,
    restanteMs,
    etiqueta: restanteMs === 0 ? '' : formatearEspera(restanteMs),
  };
}

/**
 * "1 h 59 min" cuando falta más de una hora; "59:45" cuando falta menos.
 *
 * Dos formatos: con dos horas por delante los segundos son ruido —nadie espera
 * mirando—, pero abajo de la hora el mm:ss es lo que convierte la espera en algo
 * que se puede estimar.
 */
function formatearEspera(ms: number): string {
  const totalSeg = Math.ceil(ms / 1000);
  if (totalSeg >= 3600) {
    const horas = Math.floor(totalSeg / 3600);
    const minutos = Math.ceil((totalSeg % 3600) / 60);
    if (minutos === 60) return `${horas + 1} h`;
    return minutos === 0 ? `${horas} h` : `${horas} h ${minutos} min`;
  }
  const m = Math.floor(totalSeg / 60);
  const s = totalSeg % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}
